import { isCardResult, Question, SlideComponentCharacter } from '@/types/slide';
import { gql, useQuery } from '@apollo/client';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { Box, Skeleton, Stack } from '@mui/material';
import { CardContainer, CardDisplay, CardDisplayProps } from '../displays/CardDisplay';

const shadowGlow = '0 0 4px 4px';

const DISPLAY_SIZE = {
    width: 200,
    height: 200,
};

type GetCharacterResponse = {
    character: {
        id: string;
        cardImg1: string;
        cardImg2: string;
        cardType: number;
        character: string;
        serial1: string[];
        serial2: string[];
        code: string;
        locale: string;
    };
};

export const GET_CHARACTER = gql`
    query Character($where: CharacterWhereUniqueInput!) {
        character(where: $where) {
            id
            cardImg1
            cardImg2
            cardType
            character
            serial1
            serial2
            code
            locale
        }
    }
`;

const NoAnswerCardDisplay = (props: CardDisplayProps) => {
    return (
        <CardDisplay sx={(theme) => ({ boxShadow: `${shadowGlow} ${(theme.vars || theme).palette.warning.light}` })} aria-label='no-answer-card' {...props}>
            <QuestionMarkIcon sx={(theme) => ({ width: 60, height: 60, color: (theme.vars || theme).palette.grey[800] })} />
        </CardDisplay>
    );
};

export function ResultDisplayCard({ question }: { question: Question & SlideComponentCharacter }) {
    const answer = question.answers?.[0];
    const code = question.content.characterList?.[question.content.character]?.code;

    const { data, loading } = useQuery<GetCharacterResponse>(GET_CHARACTER, {
        variables: { where: { code: code } },
    });

    const content = answer?.data ? JSON.parse(answer.data) : undefined;
    const result = content && isCardResult(content) ? content : undefined;

    const locale = data?.character?.locale ?? 'zh-HK';
    const serial1 = question.content.gameType === 'OneCard' ? data?.character?.serial1?.[0] : result?.card?.detectedCard1?.serial;
    const serial2 = result?.card?.detectedCard2?.serial;
    const img1 = `/asset/card/${locale}/${serial1}.png`;
    const img2 = `/asset/card/${locale}/${serial2}.png`;
    const direction = data?.character?.cardType === 2 ? 'column' : 'row';
    const showOrder = data?.character?.cardType === 3;
    /**
     * note that cardType 3 doesn't care about submit order
     */
    let card1Orientation = result?.card?.detectedCard1?.orientation;

    if (question.content.gameType === 'OneCard') {
        // directly show answer for the first card
        if (data?.character?.cardType === 2) {
            // is always right when only one card type
            card1Orientation = 'Right';
        } else {
            // is always up when only one card type
            card1Orientation = 'Up';
        }
    }
    const card2Orientation = result?.card?.detectedCard2?.orientation;
    const isCard1Correct: boolean | undefined =
        question.content.gameType === 'OneCard' ? undefined : !!result?.card?.detectedCard1?.serial && !!data?.character?.serial1 && data.character.serial1.includes(result.card.detectedCard1.serial);
    const isCard2Correct: boolean | undefined = !!result?.card?.detectedCard2?.serial && !!data?.character?.serial2 && data.character.serial2.includes(result.card.detectedCard2.serial);

    return loading ? (
        <Skeleton variant='rectangular' width={DISPLAY_SIZE.width} height={DISPLAY_SIZE.height} aria-label='skeleton' />
    ) : (
        <Box
            sx={{
                width: DISPLAY_SIZE.width,
                height: DISPLAY_SIZE.height,
            }}
        >
            <Stack spacing={1.5} direction={direction} sx={{ height: '100%', width: '100%' }}>
                <CardContainer>
                    {serial1 ? (
                        <CardDisplay
                            direction={direction}
                            orientation={card1Orientation}
                            imgUrl={img1}
                            order={showOrder ? 1 : undefined}
                            sx={(theme) => ({
                                boxShadow:
                                    isCard1Correct === true
                                        ? `${shadowGlow} ${(theme.vars || theme).palette.success.light}`
                                        : isCard1Correct === false
                                          ? `${shadowGlow} ${(theme.vars || theme).palette.error.light}`
                                          : theme.shadows[1],
                            })}
                            aria-label='card1'
                        />
                    ) : (
                        <NoAnswerCardDisplay direction={direction} />
                    )}
                </CardContainer>
                <CardContainer>
                    {serial2 ? (
                        <CardDisplay
                            direction={direction}
                            orientation={card2Orientation}
                            imgUrl={img2}
                            order={showOrder ? 2 : undefined}
                            sx={(theme) => ({
                                boxShadow: isCard2Correct === true ? `${shadowGlow} ${(theme.vars || theme).palette.success.light}` : `${shadowGlow} ${(theme.vars || theme).palette.error.light}`,
                            })}
                            aria-label='card2'
                        />
                    ) : (
                        <NoAnswerCardDisplay direction={direction} />
                    )}
                </CardContainer>
            </Stack>
        </Box>
    );
}
